import * as THREE from "three";
import { mulberry32 } from "../core/rng";
import { AIR, isSolid, LEAVES, LOG, WATER } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import { ARROW, BONE, BOW, GUNPOWDER, makeStack, SPIDER_EYE, STRING, type ItemStack } from "../world/items";
import {
  isMobLoaded,
  mobWaterInfo,
  MOB_GRAVITY,
  MOB_JUMP_SPEED,
  MOB_WATER_JUMP_SPEED,
  MOB_MAX_STEP,
  MOB_TERMINAL_VELOCITY,
  moveMob,
} from "./mob-physics";
import type { BlockView } from "./dropped-items";

/**
 * Hostile mob simulation.
 *
 * Zombies and skeletons are deliberately renderer-free, like dropped items and passive
 * animals. They only spawn on dry, sky-exposed ground at night, chase or shoot at the
 * player when close, and burn away after sunrise. The save format does not
 * include them: quitting at night and continuing simply lets the saved night
 * spawn a fresh wave instead of restoring monsters inside the player's base.
 */

export const MAX_HOSTILES = 8;
/** Seconds between natural spawn attempts while it is dark. */
export const HOSTILE_SPAWN_INTERVAL = 8;
export const HOSTILE_SPAWN_MIN_DISTANCE = 18;
export const HOSTILE_SPAWN_MAX_DISTANCE = 40;
/** Hostiles farther than this from the player are quietly culled. */
export const HOSTILE_DESPAWN_DISTANCE = 112;
export const HOSTILE_AGGRO_RANGE = 16;
export const HOSTILE_ATTACK_RANGE = 1.5;
export const HOSTILE_ATTACK_COOLDOWN = 1.1;
export const HOSTILE_ATTACK_DAMAGE = 2;
export const SKELETON_SHOOT_COOLDOWN = 2.0;
export const SKELETON_ARROW_DAMAGE = 3;
/** One hostile hit may land per this many seconds, no matter how many attack. */
export const HOSTILE_PLAYER_DAMAGE_COOLDOWN = 0.5;
export const HOSTILE_HIT_FLASH_SECONDS = 0.3;
export const HOSTILE_BURN_INTERVAL = 0.5;
export const HOSTILE_BURN_DAMAGE = 2;
export const HOSTILE_GROUP_MIN = 1;
export const HOSTILE_GROUP_MAX = 2;

export type HostileKind = "zombie" | "skeleton" | "creeper" | "spider";
export const HOSTILE_KINDS: readonly HostileKind[] = ["zombie", "skeleton", "creeper", "spider"] as const;
export type HostileState = "idle" | "wander" | "chase";

export interface HostileDef {
  readonly kind: HostileKind;
  readonly name: string;
  readonly maxHealth: number;
  /** Full width of the collision box. */
  readonly width: number;
  readonly height: number;
  readonly walkSpeed: number;
  readonly chaseSpeed: number;
}

export const HOSTILE_DEFS: Readonly<Record<HostileKind, HostileDef>> = {
  zombie: {
    kind: "zombie",
    name: "Zombie",
    maxHealth: 14,
    width: 0.62,
    height: 1.82,
    walkSpeed: 1.05,
    chaseSpeed: 2.4,
  },
  skeleton: {
    kind: "skeleton",
    name: "Skeleton",
    maxHealth: 14,
    width: 0.62,
    height: 1.82,
    walkSpeed: 1.05,
    chaseSpeed: 2.1,
  },
  creeper: {
    kind: "creeper",
    name: "Creeper",
    maxHealth: 20,
    width: 0.6,
    height: 1.7,
    walkSpeed: 1.0,
    chaseSpeed: 2.2,
  },
  spider: {
    kind: "spider",
    name: "Spider",
    maxHealth: 16,
    width: 1.4,
    height: 0.9,
    walkSpeed: 1.2,
    chaseSpeed: 2.8,
  },
};

export interface Hostile {
  readonly id: number;
  readonly kind: HostileKind;
  readonly position: THREE.Vector3;
  readonly velocity: THREE.Vector3;
  health: number;
  /** Seconds alive, measured only while loaded and active. */
  age: number;
  yaw: number;
  state: HostileState;
  aiTimer: number;
  heading: number;
  jumpCooldown: number;
  /** Seconds until this mob can swing or shoot again. */
  attackTimer: number;
  /** Accumulates exposure time until the next daylight burn tick. */
  burnTimer: number;
  hurtFlash: number;
  onGround: boolean;
  /** Set for one tick when horizontal movement hit a solid block. */
  blocked: boolean;
  /** Fuse elapsed in seconds for creepers: 0 to 1.5. */
  fuse: number;
  /** -1 for idle/deflating, 1 for swelling countdown. */
  fuseState: number;
  /** Seconds until next spider leap attack. */
  leapCooldown: number;
  /** True when attacked by player, overriding day neutrality. */
  provoked: boolean;
  /** True while climbing a vertical block. */
  climbing: boolean;
}

export interface HostileAttack {
  readonly hostile: Hostile;
  readonly damage: number;
}

export interface HostileShot {
  readonly hostile: Hostile;
  readonly origin: THREE.Vector3;
  readonly direction: THREE.Vector3;
  readonly damage: number;
}

export interface HostileExplosion {
  readonly hostile: Hostile;
  readonly position: THREE.Vector3;
  readonly power: number;
}

export interface HostileFuseEvent {
  readonly hostile: Hostile;
  readonly position: THREE.Vector3;
}

export interface HostileUpdateOptions {
  /** True during the dark half of the day/night cycle. */
  night: boolean;
  /** Tests and creative mode can opt out without clearing the population. */
  allowSpawn?: boolean;
}

export interface HostileHit {
  hostile: Hostile;
  distance: number;
}

export interface HostilesOptions {
  /** Tests can turn this off when they want to place every zombie by hand. */
  naturalSpawning?: boolean;
}

/** The slice of World used by the hostile simulation. */
export interface HostileWorld extends BlockView {
  /** Check if a position is within safe distance of an active torch. */
  isNearTorch?(wx: number, wy: number, wz: number, maxDist?: number): boolean;
}

export class Hostiles {
  readonly hostiles: Hostile[] = [];

  private nextId = 1;
  private readonly rand: () => number;
  private readonly naturalSpawning: boolean;
  private currentIsNight = true;
  private spawnTimer = 1.5;
  private playerHitCooldown = 0;
  private readonly pendingShots: HostileShot[] = [];
  private readonly pendingExplosions: HostileExplosion[] = [];
  private readonly pendingFuseSounds: HostileFuseEvent[] = [];
  private readonly ray = new THREE.Ray();
  private readonly rayDirection = new THREE.Vector3();
  private readonly box = new THREE.Box3();
  private readonly boxPoint = new THREE.Vector3();

  constructor(
    private readonly world: HostileWorld,
    seed = 1,
    options: HostilesOptions = {},
  ) {
    // Keep the hostile stream independent from animals and dropped items.
    this.rand = mulberry32((seed ^ 0x2f6e2b1) >>> 0);
    this.naturalSpawning = options.naturalSpawning ?? true;
  }

  /** Add one zombie. Returns null at the population cap or on invalid input. */
  spawn(
    kind: HostileKind,
    x: number,
    y: number,
    z: number,
    options: { health?: number; age?: number; yaw?: number } = {},
  ): Hostile | null {
    if (this.hostiles.length >= MAX_HOSTILES) return null;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
    if (y < 0 || y > WORLD_HEIGHT + 16) return null;

    const def = HOSTILE_DEFS[kind];
    const requestedHealth = options.health ?? def.maxHealth;
    if (!Number.isFinite(requestedHealth) || requestedHealth <= 0) return null;
    const health = Math.min(def.maxHealth, requestedHealth);
    const age = Number.isFinite(options.age) && options.age! > 0 ? options.age! : 0;
    const yaw =
      Number.isFinite(options.yaw) && options.yaw !== undefined
        ? options.yaw
        : this.rand() * Math.PI * 2;

    const hostile: Hostile = {
      id: this.nextId++,
      kind,
      position: new THREE.Vector3(x, y, z),
      velocity: new THREE.Vector3(),
      health,
      age,
      yaw,
      state: "idle",
      aiTimer: 0.4 + this.rand() * 1.8,
      heading: yaw,
      jumpCooldown: 0,
      attackTimer: 0.45 + this.rand() * 0.45,
      burnTimer: 0,
      hurtFlash: 0,
      onGround: false,
      blocked: false,
      fuse: 0,
      fuseState: -1,
      leapCooldown: 0,
      provoked: false,
      climbing: false,
    };
    this.hostiles.push(hostile);
    return hostile;
  }

  /**
   * Advance spawning, daylight burns, AI, physics and attacks. Returns the hits
   * that should be applied to the player this frame; the game owns survival
   * rules and the death message.
   */
  update(
    dt: number,
    playerPosition: THREE.Vector3,
    options: HostileUpdateOptions,
  ): HostileAttack[] {
    const attacks: HostileAttack[] = [];
    if (!Number.isFinite(dt) || dt <= 0) return attacks;
    if (!Number.isFinite(playerPosition.x) || !Number.isFinite(playerPosition.z)) {
      return attacks;
    }

    let remaining = Math.min(dt, 0.5);
    while (remaining > 0) {
      const step = Math.min(remaining, MOB_MAX_STEP);
      this.playerHitCooldown = Math.max(0, this.playerHitCooldown - step);
      const attack = this.step(step, playerPosition, options);
      if (attack) attacks.push(attack);
      remaining -= step;
    }
    return attacks;
  }

  /**
   * Ray/AABB test against every loaded hostile. The game compares this with the
   * block and animal rays so a zombie behind a wall cannot be hit.
   */
  raycast(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    maxDistance: number,
  ): HostileHit | null {
    if (!Number.isFinite(maxDistance) || maxDistance <= 0) return null;
    if (direction.lengthSq() < 1e-12) return null;

    this.rayDirection.copy(direction).normalize();
    this.ray.set(origin, this.rayDirection);

    let best: Hostile | null = null;
    let bestDistance = maxDistance;

    for (const hostile of this.hostiles) {
      if (!this.isLoaded(hostile.position.x, hostile.position.z)) continue;
      const def = HOSTILE_DEFS[hostile.kind];
      const half = def.width / 2;
      this.box.min.set(
        hostile.position.x - half,
        hostile.position.y,
        hostile.position.z - half,
      );
      this.box.max.set(
        hostile.position.x + half,
        hostile.position.y + def.height,
        hostile.position.z + half,
      );
      const hit = this.ray.intersectBox(this.box, this.boxPoint);
      if (!hit) continue;
      const distance = origin.distanceTo(hit);
      if (distance <= bestDistance) {
        best = hostile;
        bestDistance = distance;
      }
    }

    return best ? { hostile: best, distance: bestDistance } : null;
  }

  /**
   * Apply melee damage and knockback. Returns true when the zombie was killed;
   * hostiles deliberately have no loot in this slice.
   */
  damage(hostile: Hostile, amount: number, source: THREE.Vector3): boolean {
    const index = this.hostiles.indexOf(hostile);
    if (index < 0 || !Number.isFinite(amount) || amount <= 0) return false;

    hostile.health = Math.max(0, hostile.health - amount);
    hostile.hurtFlash = HOSTILE_HIT_FLASH_SECONDS;
    hostile.provoked = true;
    if (hostile.health <= 0) {
      this.hostiles.splice(index, 1);
      return true;
    }

    const dx = hostile.position.x - source.x;
    const dz = hostile.position.z - source.z;
    const length = Math.hypot(dx, dz);
    if (length > 1e-4) {
      hostile.heading = Math.atan2(dx, dz);
      hostile.velocity.x += (dx / length) * 5.2;
      hostile.velocity.z += (dz / length) * 5.2;
    } else {
      hostile.heading = this.rand() * Math.PI * 2;
      hostile.velocity.x += Math.sin(hostile.heading) * 4.2;
      hostile.velocity.z += Math.cos(hostile.heading) * 4.2;
    }
    hostile.velocity.y = Math.max(hostile.velocity.y, 4.8);
    hostile.state = "chase";
    hostile.aiTimer = 0.5;
    return false;
  }

  takePendingShots(): HostileShot[] {
    if (this.pendingShots.length === 0) return [];
    const shots = [...this.pendingShots];
    this.pendingShots.length = 0;
    return shots;
  }

  takePendingExplosions(): HostileExplosion[] {
    if (this.pendingExplosions.length === 0) return [];
    const explosions = [...this.pendingExplosions];
    this.pendingExplosions.length = 0;
    return explosions;
  }

  takePendingFuseSounds(): HostileFuseEvent[] {
    if (this.pendingFuseSounds.length === 0) return [];
    const events = [...this.pendingFuseSounds];
    this.pendingFuseSounds.length = 0;
    return events;
  }

  rollDrops(kind: HostileKind): ItemStack[] {
    const drops: ItemStack[] = [];
    if (kind === "skeleton") {
      const arrowCount = Math.floor(this.rand() * 3); // 0-2 arrows
      if (arrowCount > 0) drops.push(makeStack(ARROW, arrowCount));
      const boneCount = Math.floor(this.rand() * 3); // 0-2 bones
      if (boneCount > 0) drops.push(makeStack(BONE, boneCount));
      if (this.rand() < 0.085) {
        // Rare bow drop with partial durability
        const durability = Math.floor(30 + this.rand() * 120);
        drops.push({ id: BOW, count: 1, durability });
      }
    } else if (kind === "creeper") {
      const gunpowderCount = Math.floor(this.rand() * 3); // 0-2 gunpowder
      if (gunpowderCount > 0) drops.push(makeStack(GUNPOWDER, gunpowderCount));
    } else if (kind === "spider") {
      const stringCount = Math.floor(this.rand() * 3); // 0-2 string
      if (stringCount > 0) drops.push(makeStack(STRING, stringCount));
      if (this.rand() < 0.33) {
        drops.push(makeStack(SPIDER_EYE, 1)); // 1 spider eye (33% drop chance)
      }
    }
    return drops;
  }

  clear(): void {
    this.hostiles.length = 0;
    this.pendingShots.length = 0;
    this.pendingExplosions.length = 0;
    this.pendingFuseSounds.length = 0;
    this.nextId = 1;
    this.spawnTimer = 1.5;
    this.playerHitCooldown = 0;
  }

  private step(
    dt: number,
    playerPosition: THREE.Vector3,
    options: HostileUpdateOptions,
  ): HostileAttack | null {
    this.currentIsNight = options.night;
    if (this.naturalSpawning && (options.allowSpawn ?? true) && options.night) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = HOSTILE_SPAWN_INTERVAL;
        if (this.hostiles.length < MAX_HOSTILES) this.tryNaturalSpawn(playerPosition);
      }
    } else if (this.spawnTimer <= 0) {
      this.spawnTimer = HOSTILE_SPAWN_INTERVAL;
    }

    let attack: HostileAttack | null = null;

    for (let i = this.hostiles.length - 1; i >= 0; i--) {
      const hostile = this.hostiles[i];
      const dx = hostile.position.x - playerPosition.x;
      const dz = hostile.position.z - playerPosition.z;
      if (dx * dx + dz * dz > HOSTILE_DESPAWN_DISTANCE * HOSTILE_DESPAWN_DISTANCE) {
        this.hostiles.splice(i, 1);
        continue;
      }

      // Chunks outside the render disc are frozen: no aging, burning or AI.
      if (!this.isLoaded(hostile.position.x, hostile.position.z)) continue;

      hostile.age += dt;
      hostile.hurtFlash = Math.max(0, hostile.hurtFlash - dt);
      hostile.jumpCooldown = Math.max(0, hostile.jumpCooldown - dt);
      hostile.attackTimer = Math.max(0, hostile.attackTimer - dt);

      if (!options.night && this.applyDaylightBurn(hostile, dt)) {
        this.hostiles.splice(i, 1);
        continue;
      }

      this.updateAi(hostile, dt, playerPosition);
      if (hostile.health <= 0) {
        this.hostiles.splice(i, 1);
        continue;
      }
      this.applyPhysics(hostile, dt);
      this.move(hostile, dt);

      if (hostile.position.y < -8) {
        this.hostiles.splice(i, 1);
        continue;
      }

      if (
        (hostile.kind === "zombie" || hostile.kind === "spider") &&
        this.inAttackRange(hostile, playerPosition) &&
        hostile.attackTimer <= 0
      ) {
        hostile.attackTimer = HOSTILE_ATTACK_COOLDOWN;
        if (this.playerHitCooldown <= 0) {
          this.playerHitCooldown = HOSTILE_PLAYER_DAMAGE_COOLDOWN;
          attack = { hostile, damage: HOSTILE_ATTACK_DAMAGE };
        }
      }
    }

    return attack;
  }

  private tryNaturalSpawn(playerPosition: THREE.Vector3): void {
    for (let attempt = 0; attempt < 4 && this.hostiles.length < MAX_HOSTILES; attempt++) {
      const angle = this.rand() * Math.PI * 2;
      const distance =
        HOSTILE_SPAWN_MIN_DISTANCE +
        this.rand() * (HOSTILE_SPAWN_MAX_DISTANCE - HOSTILE_SPAWN_MIN_DISTANCE);
      const x = Math.floor(playerPosition.x + Math.cos(angle) * distance);
      const z = Math.floor(playerPosition.z + Math.sin(angle) * distance);
      if (!this.isLoaded(x, z)) continue;

      const y = this.findSpawnColumn(x, z);
      if (y === null) continue;

      const group =
        HOSTILE_GROUP_MIN +
        Math.floor(this.rand() * (HOSTILE_GROUP_MAX - HOSTILE_GROUP_MIN + 1));
      let created = 0;

      for (let i = 0; i < group && this.hostiles.length < MAX_HOSTILES; i++) {
        const ox = i === 0 ? 0 : (this.rand() * 2 - 1) * 2.6;
        const oz = i === 0 ? 0 : (this.rand() * 2 - 1) * 2.6;
        const sx = i === 0 ? x : Math.floor(x + ox);
        const sz = i === 0 ? z : Math.floor(z + oz);
        const sy = i === 0 ? y : this.findSpawnColumn(sx, sz);
        if (sy === null) continue;
        const roll = this.rand();
        const kind: HostileKind =
          roll < 0.25 ? "spider" : roll < 0.5 ? "creeper" : roll < 0.75 ? "skeleton" : "zombie";
        if (this.spawn(kind, sx + 0.5, sy, sz + 0.5)) created++;
      }

      if (created > 0) return;
    }
  }

  private updateAi(
    hostile: Hostile,
    dt: number,
    playerPosition: THREE.Vector3,
  ): void {
    const dx = playerPosition.x - hostile.position.x;
    const dz = playerPosition.z - hostile.position.z;
    const distanceSq = dx * dx + dz * dz;

    if (hostile.kind === "creeper") {
      if (distanceSq <= HOSTILE_AGGRO_RANGE * HOSTILE_AGGRO_RANGE) {
        hostile.state = "chase";
        const dist = Math.sqrt(distanceSq);
        const angleToPlayer = Math.atan2(dx, dz);
        hostile.yaw = angleToPlayer;
        hostile.heading = angleToPlayer;

        // Swelling threshold: <= 3 blocks triggers hissing fuse
        if (dist <= 3.0) {
          if (hostile.fuseState <= 0) {
            hostile.fuseState = 1;
            this.pendingFuseSounds.push({
              hostile,
              position: hostile.position.clone(),
            });
          }
        } else if (dist > 7.0) {
          // Escaped radius cancels fuse
          hostile.fuseState = -1;
        }
      } else {
        hostile.fuseState = -1;
        if (hostile.state === "chase") {
          hostile.state = "idle";
          hostile.aiTimer = 0.5 + this.rand();
        }
        hostile.aiTimer -= dt;
        if (hostile.aiTimer <= 0) {
          if (this.rand() < 0.3) {
            hostile.state = "idle";
            hostile.aiTimer = 1 + this.rand() * 2.6;
          } else {
            hostile.state = "wander";
            hostile.heading = this.rand() * Math.PI * 2;
            hostile.aiTimer = 2 + this.rand() * 3.5;
          }
        }
      }

      // Progress or regress fuse
      if (hostile.fuseState > 0) {
        hostile.fuse += dt;
        if (hostile.fuse >= 1.5) {
          // Detonate!
          this.pendingExplosions.push({
            hostile,
            position: hostile.position.clone(),
            power: 3.0,
          });
          hostile.health = 0;
        }
      } else if (hostile.fuse > 0) {
        hostile.fuse = Math.max(0, hostile.fuse - dt * 1.5);
      }
    } else if (hostile.kind === "skeleton") {
      if (distanceSq <= HOSTILE_AGGRO_RANGE * HOSTILE_AGGRO_RANGE) {
        hostile.state = "chase";
        const dist = Math.sqrt(distanceSq);
        const angleToPlayer = Math.atan2(dx, dz);
        hostile.yaw = angleToPlayer;

        // Ranged distancing & strafe
        if (dist > 13) {
          hostile.heading = angleToPlayer;
        } else if (dist < 6) {
          hostile.heading = angleToPlayer + Math.PI; // back up
        } else {
          // Strafe perpendicular to player
          const strafe = (hostile.id % 2 === 0 ? 1 : -1) * 1.3;
          hostile.heading = angleToPlayer + strafe;
        }

        // Ranged attack
        if (dist <= HOSTILE_AGGRO_RANGE && hostile.attackTimer <= 0) {
          hostile.attackTimer = SKELETON_SHOOT_COOLDOWN + this.rand() * 0.6;
          const origin = hostile.position.clone().add(new THREE.Vector3(0, 1.35, 0));
          const target = playerPosition.clone().add(new THREE.Vector3(0, 0.9, 0));
          const shotDir = target.sub(origin);
          shotDir.y += 0.04 * dist; // slight arc compensation
          shotDir.normalize();
          this.pendingShots.push({
            hostile,
            origin,
            direction: shotDir,
            damage: SKELETON_ARROW_DAMAGE,
          });
        }
      } else {
        if (hostile.state === "chase") {
          hostile.state = "idle";
          hostile.aiTimer = 0.5 + this.rand();
        }
        hostile.aiTimer -= dt;
        if (hostile.aiTimer <= 0) {
          if (this.rand() < 0.3) {
            hostile.state = "idle";
            hostile.aiTimer = 1 + this.rand() * 2.6;
          } else {
            hostile.state = "wander";
            hostile.heading = this.rand() * Math.PI * 2;
            hostile.aiTimer = 2 + this.rand() * 3.5;
          }
        }
      }
    } else if (hostile.kind === "spider") {
      const isDaylight = !this.currentIsNight && this.isSkyExposed(hostile);
      const aggressive = !isDaylight || hostile.provoked;

      if (aggressive && distanceSq <= HOSTILE_AGGRO_RANGE * HOSTILE_AGGRO_RANGE) {
        hostile.state = "chase";
        const dist = Math.sqrt(distanceSq);
        const angleToPlayer = Math.atan2(dx, dz);
        hostile.yaw = angleToPlayer;
        hostile.heading = angleToPlayer;

        // Leap attack towards player: in Minecraft, spiders leap when 2 to 4.5 blocks away and onGround
        hostile.leapCooldown = Math.max(0, hostile.leapCooldown - dt);
        if (dist >= 2.0 && dist <= 4.5 && hostile.onGround && hostile.leapCooldown <= 0) {
          hostile.velocity.x += (dx / dist) * 4.4;
          hostile.velocity.z += (dz / dist) * 4.4;
          hostile.velocity.y = 5.2;
          hostile.leapCooldown = 1.8 + this.rand() * 0.8;
          hostile.onGround = false;
        }
      } else {
        if (hostile.state === "chase") {
          hostile.state = "idle";
          hostile.aiTimer = 0.5 + this.rand();
        }
        hostile.aiTimer -= dt;
        if (hostile.aiTimer <= 0) {
          if (this.rand() < 0.25) {
            hostile.state = "idle";
            hostile.aiTimer = 1 + this.rand() * 2.5;
          } else {
            hostile.state = "wander";
            hostile.heading = this.rand() * Math.PI * 2;
            hostile.aiTimer = 2 + this.rand() * 3.5;
          }
        }
      }
    } else {
      if (distanceSq <= HOSTILE_AGGRO_RANGE * HOSTILE_AGGRO_RANGE) {
        hostile.state = "chase";
        hostile.heading = Math.atan2(dx, dz);
        hostile.aiTimer = 0.8;
      } else {
        if (hostile.state === "chase") {
          hostile.state = "idle";
          hostile.aiTimer = 0.4 + this.rand() * 1.4;
        }
        hostile.aiTimer -= dt;
        if (hostile.aiTimer <= 0) {
          if (this.rand() < 0.3) {
            hostile.state = "idle";
            hostile.aiTimer = 1 + this.rand() * 2.6;
          } else {
            hostile.state = "wander";
            hostile.heading = this.rand() * Math.PI * 2;
            hostile.aiTimer = 2 + this.rand() * 3.5;
          }
        }
      }
    }

    const def = HOSTILE_DEFS[hostile.kind];
    const inWater = mobWaterInfo(this.world, hostile.position, def.height) !== null;
    let speed =
      hostile.state === "chase"
        ? def.chaseSpeed
        : hostile.state === "wander"
          ? def.walkSpeed
          : 0;

    if (hostile.kind === "creeper" && hostile.fuseState > 0) {
      speed = 0; // Freeze movement while swelling
    }

    if (inWater) {
      speed *= 0.55; // Water resistance slows movement
    }

    if (inWater && hostile.state === "chase" && hostile.onGround && hostile.jumpCooldown <= 0) {
      if (playerPosition.y > hostile.position.y + 0.3) {
        hostile.velocity.y = 2.8;
        hostile.jumpCooldown = 1.0;
        hostile.onGround = false;
      }
    }

    if (speed > 0 && hostile.state === "wander" && hostile.onGround) {
      if (this.shouldTurn(hostile, inWater)) {
        hostile.heading = this.rand() * Math.PI * 2;
        hostile.aiTimer = 1.5 + this.rand() * 2;
      }
    }

    const targetX = Math.sin(hostile.heading) * speed;
    const targetZ = Math.cos(hostile.heading) * speed;
    const blend = Math.min(1, dt * (hostile.state === "chase" ? 10 : 7));
    hostile.velocity.x += (targetX - hostile.velocity.x) * blend;
    hostile.velocity.z += (targetZ - hostile.velocity.z) * blend;
    if (speed > 0.01) hostile.yaw = hostile.heading;
  }

  /** Look one block ahead for water, a wall or a cliff while wandering. */
  private shouldTurn(hostile: Hostile, inWater = false): boolean {
    const aheadX = hostile.position.x + Math.sin(hostile.heading) * 0.95;
    const aheadZ = hostile.position.z + Math.cos(hostile.heading) * 0.95;
    if (!this.isLoaded(aheadX, aheadZ)) return true;

    const def = HOSTILE_DEFS[hostile.kind];
    const x = Math.floor(aheadX);
    const z = Math.floor(aheadZ);
    const feetY = Math.floor(hostile.position.y + 0.05);
    const headY = Math.floor(hostile.position.y + def.height - 0.05);

    if (inWater) {
      // In water: only turn if blocked by a solid wall 2+ blocks high above feet level
      for (let y = feetY + 2; y <= headY + 1; y++) {
        if (isSolid(this.world.peekBlock(x, y, z))) return true;
      }
      return false;
    }

    if (hostile.kind === "spider") {
      // Spiders climb walls, so they only avoid water or world bounds while wandering
      for (let y = feetY; y <= headY; y++) {
        if (this.world.peekBlock(x, y, z) === WATER) return true;
      }
      return false;
    }

    // On land: avoid water, walls and cliffs
    for (let y = feetY; y <= headY; y++) {
      const block = this.world.peekBlock(x, y, z);
      if (block === WATER) return true;
      if (isSolid(block)) return true;
    }

    let ground = false;
    for (let y = feetY - 1; y >= feetY - 3; y--) {
      const block = this.world.peekBlock(x, y, z);
      if (block === WATER) return true;
      if (isSolid(block)) {
        ground = true;
        break;
      }
    }
    return !ground;
  }

  private applyPhysics(hostile: Hostile, dt: number): void {
    if (hostile.kind === "spider" && hostile.climbing) {
      // While climbing, gravity is neutralized
      return;
    }
    const def = HOSTILE_DEFS[hostile.kind];
    const water = mobWaterInfo(this.world, hostile.position, def.height);

    if (water) {
      if (hostile.velocity.y > 0.5) {
        // Jumping upward (e.g. climbing onto shore or jumping a ledge): apply gravity
        hostile.velocity.y -= MOB_GRAVITY * dt;
        return;
      }
      // In real Minecraft, zombies DO NOT float on water. They sink steadily.
      hostile.velocity.y -= MOB_GRAVITY * 0.25 * dt;
      if (hostile.velocity.y < -1.6) {
        hostile.velocity.y = -1.6; // terminal sinking speed in water
      }
      const drag = Math.max(0, 1 - dt * 2.8);
      hostile.velocity.x *= drag;
      hostile.velocity.z *= drag;
      return;
    }

    hostile.velocity.y -= MOB_GRAVITY * dt;
    if (hostile.velocity.y < -MOB_TERMINAL_VELOCITY) {
      hostile.velocity.y = -MOB_TERMINAL_VELOCITY;
    }
  }

  private move(hostile: Hostile, dt: number): void {
    const def = HOSTILE_DEFS[hostile.kind];
    const isSpider = hostile.kind === "spider";
    moveMob(this.world, hostile, dt, def.width, def.height, isSpider);

    if (isSpider) {
      if (hostile.blocked) {
        // Spider wall climbing: in Minecraft Java Edition, EntitySpider climbs vertical blocks when moving against them
        hostile.velocity.y = 4.0;
        hostile.climbing = true;
      } else {
        hostile.climbing = false;
      }
    }

    const inWater = mobWaterInfo(this.world, hostile.position, def.height) !== null;
    if (hostile.blocked && (hostile.onGround || inWater) && hostile.jumpCooldown <= 0) {
      // A chasing or wading zombie climbs a one-block lip or hops onto shore instead of stopping dead.
      hostile.velocity.y = inWater ? MOB_WATER_JUMP_SPEED : MOB_JUMP_SPEED;
      if (inWater) {
        hostile.velocity.x += Math.sin(hostile.heading) * 1.6;
        hostile.velocity.z += Math.cos(hostile.heading) * 1.6;
      }
      hostile.jumpCooldown = 0.8;
      hostile.onGround = false;
    }
  }

  private inAttackRange(hostile: Hostile, playerPosition: THREE.Vector3): boolean {
    const dx = hostile.position.x - playerPosition.x;
    const dz = hostile.position.z - playerPosition.z;
    if (dx * dx + dz * dz > HOSTILE_ATTACK_RANGE * HOSTILE_ATTACK_RANGE) return false;
    return Math.abs(hostile.position.y - playerPosition.y) <= 1.8;
  }

  /** Burn exposed zombies after sunrise; water and shade stop the burn. Creepers and Spiders do not burn. */
  private applyDaylightBurn(hostile: Hostile, dt: number): boolean {
    if (hostile.kind === "creeper" || hostile.kind === "spider") return false;
    const def = HOSTILE_DEFS[hostile.kind];
    const feet = this.world.peekBlock(
      Math.floor(hostile.position.x),
      Math.floor(hostile.position.y + 0.05),
      Math.floor(hostile.position.z),
    );
    const inWater = feet === WATER || mobWaterInfo(this.world, hostile.position, def.height) !== null;
    if (inWater || !this.isSkyExposed(hostile)) {
      hostile.burnTimer = 0;
      return false;
    }

    hostile.burnTimer += dt;
    if (hostile.burnTimer < HOSTILE_BURN_INTERVAL) return false;
    hostile.burnTimer -= HOSTILE_BURN_INTERVAL;
    hostile.health = Math.max(0, hostile.health - HOSTILE_BURN_DAMAGE);
    hostile.hurtFlash = HOSTILE_HIT_FLASH_SECONDS;
    return hostile.health <= 0;
  }

  private isSkyExposed(hostile: Hostile): boolean {
    const def = HOSTILE_DEFS[hostile.kind];
    const x = Math.floor(hostile.position.x);
    const z = Math.floor(hostile.position.z);
    const startY = Math.min(
      WORLD_HEIGHT - 1,
      Math.floor(hostile.position.y + def.height),
    );
    for (let y = startY; y < WORLD_HEIGHT; y++) {
      if (isSolid(this.world.peekBlock(x, y, z))) return false;
    }
    return true;
  }

  /** Highest dry, open standing position in a loaded column. */
  private findSpawnColumn(wx: number, wz: number): number | null {
    if (!this.isLoaded(wx, wz)) return null;
    for (let y = WORLD_HEIGHT - 1; y >= 1; y--) {
      const block = this.world.peekBlock(wx, y, wz);
      if (block === AIR) continue;
      if (block === WATER) return null;
      if (!isSolid(block)) return null;
      if (block === LEAVES || block === LOG) return null;
      if (this.world.peekBlock(wx, y + 1, wz) !== AIR) return null;
      if (this.world.peekBlock(wx, y + 2, wz) !== AIR) return null;
      if (this.world.isNearTorch?.(wx, y + 1, wz, 10)) return null;
      return y + 1;
    }
    return null;
  }

  private isLoaded(x: number, z: number): boolean {
    return isMobLoaded(this.world, x, z);
  }
}
